import React, { useState } from "react";
import ConditionRenderer from "./ConditionRender";
import { useSelector } from "react-redux";
import TableConditionRenderer from "./TableRenderConditionContent";

// Define the type for a condition item
interface ConditionItem {
  itemNo: string;
  item: string;
  isBlankExist: boolean;
  blank: any[];
  subItem: {
    subitemNo: string;
    subItemSno: string;
    subItemVal: string;
  }[];
  isTableExist: boolean;
  tableItems: any[];
  isUpdated?: boolean;
  status?: 'normal' | 'deleted' | 'updated' | 'added';
  [key: string]: any; // For dynamic table fields
}

interface ConditionsTableProps {
  conditionsData: ConditionItem[];
  onConditionsChange: (newConditions: ConditionItem[]) => void;
  originalData: ConditionItem[];
  checked?: any;
  reviewedData1?: any;
  isReadOnly?: boolean;
  condFields?: any;
  setCondFields?: any;
  setFormErrors?: any;
  getCondTableData?: any;
}

const ConditionsTable: React.FC<ConditionsTableProps> = ({
  conditionsData,
  onConditionsChange,
  originalData,
  checked,
  reviewedData1,
  isReadOnly = false,
  condFields,
  setCondFields,
  setFormErrors,
  getCondTableData
}) => {
  const [editingCondition, setEditingCondition] = useState<string | null>(null);

  const userDetail = useSelector((state: any) => state.auth.userDetails);
  const roleIdentifier = userDetail?.Role?.Role_Identifier;

  // Determine which data to render - prioritize conditionsData from props
  const dataToRender = (conditionsData && conditionsData.length > 0)
    ? conditionsData
    : (reviewedData1?.uploadedText?.Cultivation || []);

  // Function to handle editing a condition
  const handleEditCondition = (itemNo: string) => {
    setEditingCondition(itemNo);
  };

  // Function to handle undo delete
  const handleUndoDelete = (itemNo: string) => {
    const originalItem = originalData.find(item => item.itemNo === itemNo);

    if (originalItem) {
      // Restore to original state
      const updatedConditions = conditionsData.map((item) =>
        item.itemNo === itemNo
          ? { ...originalItem, status: 'normal' as const }
          : item
      );
      onConditionsChange(updatedConditions);
    } else {
      // If original not found, restore with basic structure
      const updatedConditions = conditionsData.map((item) =>
        item.itemNo === itemNo
          ? {
            ...item,
            item: 'Restored condition - please edit to add content',
            status: 'updated' as const,
            isBlankExist: false,
            blank: [],
            subItem: [],
            isTableExist: false,
            tableItems: []
          }
          : item
      );
      onConditionsChange(updatedConditions);
    }
  };

  // Function to save condition changes
  const handleSaveCondition = (itemNo: string) => {
    const updatedConditions = conditionsData.map((item) => {
      if (item.itemNo !== itemNo) return item;
      const nextStatus = item.status === 'added' ? 'added' as const : 'updated' as const;
      return { ...item, status: nextStatus };
    });

    onConditionsChange(updatedConditions);
    setEditingCondition(null);
  };

  // Function to cancel editing
  const handleCancelEdit = () => {
    setEditingCondition(null);
  };

  // Fixed: No longer mutates props directly
  const handleTableInputChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const { name, value } = e.target;

    const updatedConditions = conditionsData.map((item, idx) =>
      idx === index ? { ...item, [name]: value, status: item.status === 'added' ? 'added' as const : 'updated' as const } : item
    );

    onConditionsChange(updatedConditions);
  };

  // Function to delete a condition
  const handleDeleteCondition = (itemNo: string) => {
    const itemToDelete = conditionsData.find(item => item.itemNo === itemNo);

    if (itemToDelete && itemToDelete.status === 'added') {
      // If it's a newly added condition, remove it completely
      const updatedConditions = conditionsData.filter((item) => item.itemNo !== itemNo);
      onConditionsChange(updatedConditions);
    } else {
      // If it's an original condition, mark as deleted
      const updatedConditions = conditionsData.map((item) =>
        item.itemNo === itemNo
          ? { ...item, status: 'deleted' as const }
          : item
      );
      onConditionsChange(updatedConditions);
    }
  };

  // Function to add a new condition
  const handleAddCondition = () => {
    const maxItemNo = conditionsData.length > 0
      ? Math.max(...conditionsData.map((item) => parseInt(item.itemNo) || 0))
      : 0;

    const newItemNo = (maxItemNo + 1).toString();

    const newCondition: ConditionItem = {
      itemNo: newItemNo,
      item: "New condition text here...",
      isBlankExist: false,
      blank: [],
      subItem: [],
      isTableExist: false,
      tableItems: [],
      status: 'added'
    };

    onConditionsChange([...conditionsData, newCondition]);
    // Automatically enter edit mode for the new condition
    setEditingCondition(newItemNo);
  };

  const renderConditionContent = (row: ConditionItem, index: number) => {
    // Marketing Board view: render table when present; otherwise render HTML
    if (roleIdentifier !== 'SPO') {
      if (row?.status === 'deleted') {
        return <div className="text-danger fw-semibold">Condition is deleted</div>;
      }

      if (row.isTableExist && row.tableItems?.length > 0) {
        return (
          <TableConditionRenderer
            row={row}
            index={index}
            conditionsData={conditionsData}
            onTableChange={(idx, data) => {
              const updatedConditions = conditionsData.map((item, i) =>
                i === idx ? { ...item, ...data, status: item.status === 'added' ? 'added' as const : 'updated' as const } : item
              );
              onConditionsChange(updatedConditions);
            }}
            isEditing={editingCondition === row.itemNo}
            isReadOnly={isReadOnly}
          condFields={condFields}       //<---  
          setCondFields={setCondFields} //<---
          setFormErrors={setFormErrors}
          getCondTableData={getCondTableData}
          />
        );
      }
      
      return (
        <ConditionRenderer
          row={row}
          conditionsData={conditionsData}
          index={index}
          checked={checked}
          onConditionsChange={onConditionsChange}
          roleIdentifier={roleIdentifier}
          isEditing={editingCondition === row.itemNo}
          isReadOnly={isReadOnly}
          condFields={condFields}
          setCondFields={setCondFields}
          setFormErrors={setFormErrors}
        />
      );
    }

    // SPO (Sponsor) view
    if (row.isTableExist && row.tableItems?.length > 0) {
      return (
        <TableConditionRenderer
          row={row}
          index={index}
          conditionsData={conditionsData}
          onTableChange={(idx, data) => {
            const updatedConditions = conditionsData.map((item, i) =>
              i === idx ? { ...item, ...data, status: item.status === 'added' ? 'added' as const : 'updated' as const } : item
            );
            onConditionsChange(updatedConditions);
          }}
          isEditing={editingCondition === row.itemNo}
          isReadOnly={isReadOnly}
          condFields={condFields}
          setCondFields={setCondFields}
          setFormErrors={setFormErrors}
          getCondTableData={getCondTableData}
        />
      );
    }

    // Handle blanks with placeholders
    const hasBlanks = row.blank && row.blank.length > 0;
    const textContainsPlaceholders = row.item.includes("......");

    if (hasBlanks && textContainsPlaceholders) {
      let inputIndex = 0;
      const parts = row.item
        .split(/(\.{2,})/g)
        .map((part, idx) => {
          if (part.match(/\.{2,}/)) {
            if (inputIndex < row.blank.length) {
              const b = row.blank[inputIndex];
              inputIndex++;
              return (
                <input
                  key={idx}
                  type={b.blankType}
                  name={b.blankVal}
                  className="form-control d-inline-block mx-1"
                  placeholder={`Enter ${b.blankName}`}
                  style={{ width: "150px" }}
                  aria-label={`Enter ${b.blankName}`}
                  disabled={isReadOnly}
                />
              );
            }
          }
          return part;
        });

      return <div>{parts}</div>;
    }

    // Generic HTML rendering
    return (
      <ConditionRenderer
        row={row}
        conditionsData={conditionsData}
        index={index}
        checked={checked}
        onConditionsChange={onConditionsChange}
        roleIdentifier={roleIdentifier}
        isEditing={editingCondition === row.itemNo}
        isReadOnly={isReadOnly}
        condFields={condFields}
        setCondFields={setCondFields}
        setFormErrors={setFormErrors}
      />
    );
  };

  const getRowBackgroundClass = (row: ConditionItem) => {
    const itemNumber = parseInt(row?.itemNo || '0', 10);
    const isNewAfter19 = Number.isFinite(itemNumber) && itemNumber > 19;

    // Sponsor view: force green for any item after 19
    if (roleIdentifier === 'SPO' && isNewAfter19) {
      return 'bg-success text-black bg-opacity-50';
    }

    if (row?.status === 'deleted') return 'bg-danger text-black bg-opacity-50';
    if (row?.status === 'updated') return 'bg-warning text-black bg-opacity-50';
    if (row?.status === 'added') return 'bg-success text-black bg-opacity-50';

    return '';
  };

  return (
    <div
      id="table"
      style={{ fontSize: "1.25rem" }}
      className="w-100 text-black overflow-hidden border bg-white border-secondary rounded mb-5"
    >
      {/* Status Indicators Legend */}
      <div className="p-3 d-flex justify-content-between border bg-light">
        <h5 className="mb-2">Status Indicators:</h5>
        <div className="d-flex gap-4">
          <div className="d-flex align-items-center">
            <div
              className="rounded-circle me-2"
              style={{ width: '12px', height: '12px', backgroundColor: '#dc3545' }}
              aria-hidden="true"
            ></div>
            <h5>Deleted</h5>
          </div>
          <div className="d-flex align-items-center">
            <div
              className="rounded-circle me-2"
              style={{ width: '12px', height: '12px', backgroundColor: '#ffc107' }}
              aria-hidden="true"
            ></div>
            <h5>Updated</h5>
          </div>
          <div className="d-flex align-items-center">
            <div
              className="rounded-circle me-2"
              style={{ width: '12px', height: '12px', backgroundColor: '#198754' }}
              aria-hidden="true"
            ></div>
            <h5>Added</h5>
          </div>
        </div>
      </div>

      {/* Table Header */}
      <div
        id="head"
        className="d-flex w-100 border-top border-bottom border-secondary"
        style={{ backgroundColor: "#CCE3FF" }}
      >
        <h4 className="col-1 p-2 justify-content-center d-flex align-items-center border-secondary border-end">
          SNo.
        </h4>
        <h4 className="col-8 p-2 justify-content-center d-flex align-items-center border-secondary border-end">
          Field
        </h4>
        <h4 className="col-3 p-2 justify-content-center d-flex align-items-center">
          Action
        </h4>
      </div>

      {/* Table Body */}
      <div className="w-100">
        {dataToRender && dataToRender.length > 0 && dataToRender.map((row, index) => (
          <div
            key={`${row.itemNo}-${index}`}
            className={`d-flex border-bottom border-secondary ${getRowBackgroundClass(row)}`}
          >
            {/* Serial Number Column */}
            <div className="col-1 p-2 justify-content-center d-flex align-items-center border-secondary border-end fw-bold">
              {row.itemNo}
            </div>

            {/* Field Content Column */}
            <div className="col-8 p-2 border-secondary border-end">
              {renderConditionContent(row, index)}

              {/* Sub Items */}
              {row.subItem && row.subItem.length > 0 && (
                <div className="mt-3">
                  <small className="text-muted fw-semibold">Sub-items:</small>
                  {row.subItem.map((subItem, subIdx) => (
                    <div key={subIdx} className="mt-2 ps-3">
                      <strong>{subItem.subItemSno}</strong> {subItem.subItemVal}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Action Column */}
            <div className="col-3 p-2 justify-content-center d-flex align-items-center gap-1">
              {isReadOnly ? (
                <span className="text-muted small">View Only</span>
              ) : editingCondition === row.itemNo ? (
                <>
                  <button
                    className="btn btn-success"
                    onClick={() => handleSaveCondition(row.itemNo)}
                    title="Save changes"
                    aria-label="Save changes"
                  >
                    <i className="fas fa-save" aria-hidden="true"></i> Save
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={handleCancelEdit}
                    title="Cancel editing"
                    aria-label="Cancel editing"
                  >
                    <i className="fas fa-times" aria-hidden="true"></i> Cancel
                  </button>
                </>
              ) : row?.status === 'deleted' ? (
                <button
                  className="btn text-white border border-white btn-outline-success"
                  onClick={() => handleUndoDelete(row.itemNo)}
                  title="Undo deletion"
                  aria-label="Undo deletion"
                >
                  <i className="fas fa-undo me-1" aria-hidden="true"></i>Undo
                </button>
              ) : (
                <>
                  <button
                    className="btn btn-outline-info"
                    onClick={() => handleEditCondition(row.itemNo)}
                    title="Edit"
                    aria-label="Edit condition"
                  >
                    <i className="fas fa-edit" aria-hidden="true"></i> Edit
                  </button>
                  <button
                    className="btn btn-outline-danger"
                    onClick={() => handleDeleteCondition(row.itemNo)}
                    title={row?.status === 'added' ? 'Remove completely' : 'Mark as deleted'}
                    aria-label={row?.status === 'added' ? 'Remove completely' : 'Mark as deleted'}
                  >
                    <i className="fas fa-trash" aria-hidden="true"></i> {row?.status === 'added' ? 'Remove' : 'Delete'}
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add More Button for Conditions */}
      {!isReadOnly && (
        <div className="p-3 border-top bg-light">
          <button
            className="btn btn-outline-primary"
            onClick={handleAddCondition}
            aria-label="Add new condition"
          >
            <i className="fas fa-plus me-2" aria-hidden="true"></i>Add New Condition
          </button>
        </div>
      )}
    </div>
  );
};

export default ConditionsTable;